import { createContext, type ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { ConfirmDialog } from './confirm-dialog';
import { SuccessDialog } from './success-dialog';

export type ConfirmOptions = {
  title: ReactNode;
  subtitle?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
  icon?: ReactNode;
  /** If it returns a promise, the confirm button shows loading until it settles. */
  onConfirm?: () => void | Promise<void>;
};

export type SuccessOptions = {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
};

type DialogContextValue = {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  success: (opts: SuccessOptions) => void;
};

export const DialogContext = createContext<DialogContextValue | null>(null);

type ConfirmState = ConfirmOptions & { open: boolean; loading: boolean };
type SuccessState = SuccessOptions & { open: boolean };

export function DialogHost({ children }: { children: ReactNode }) {
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [successState, setSuccessState] = useState<SuccessState | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const settle = useCallback((value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setConfirmState(null);
  }, []);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setConfirmState({ ...opts, open: true, loading: false });
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    const onConfirm = confirmState?.onConfirm;
    if (onConfirm) {
      setConfirmState((prev) => (prev ? { ...prev, loading: true } : prev));
      try {
        await onConfirm();
      } finally {
        setConfirmState((prev) => (prev ? { ...prev, loading: false } : prev));
      }
    }
    settle(true);
  }, [confirmState, settle]);

  const success = useCallback((opts: SuccessOptions) => {
    setSuccessState({ ...opts, open: true });
  }, []);

  const value = useMemo<DialogContextValue>(() => ({ confirm, success }), [confirm, success]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      {confirmState ? (
        <ConfirmDialog
          open={confirmState.open}
          title={confirmState.title}
          subtitle={confirmState.subtitle}
          confirmText={confirmState.confirmText}
          cancelText={confirmState.cancelText}
          danger={confirmState.danger}
          icon={confirmState.icon}
          loading={confirmState.loading}
          onConfirm={handleConfirm}
          onCancel={() => settle(false)}
        />
      ) : null}
      {successState ? (
        <SuccessDialog
          open={successState.open}
          title={successState.title}
          subtitle={successState.subtitle}
          icon={successState.icon}
          onClose={() => setSuccessState(null)}
        />
      ) : null}
    </DialogContext.Provider>
  );
}
